'use client';

import { AnimatePresence, motion } from 'framer-motion';
import { useSearchParams } from 'next/navigation';
import React, { useEffect } from 'react';

import ForAuthorsSection from '@/components/Partnership/ForAuthorsSection';
import ForBrandsSection from '@/components/Partnership/ForBrandsSection';
import ForCollegesSection from '@/components/Partnership/ForCollegesSection';
import ForCorporatesSection from '@/components/Partnership/ForCorporatesSection';
import ForEducatorsSection from '@/components/Partnership/ForEducatorsSection';
import ForPublishersSection from '@/components/Partnership/ForPublishersSection';
import ForSchoolsSection from '@/components/Partnership/ForSchoolsSection';
import TargetedForSection from '@/components/Partnership/TargetedForSection';
import HeroSection from '@/components/V2/HomePage/HeroSection';

import { programmaticScrollTo } from '@/utils/ScrollControl/scrollControl';

const sectionAnimation = {
  hidden: {
    opacity: 0,
    y: -40,
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      delay: 0.2,
      duration: 0.6,
      ease: 'easeOut',
    },
  },
};

enum PartnershipIdEnum {
  AUTHORS = 'for-authors',
  BRANDS = 'for-brands',
  PUBLISHERS = 'for-publishers',
  CORPORATES = 'for-corporates',
  EDUCATORS = 'for-educators',
  COLLEGES = 'for-colleges',
  SCHOOLS = 'for-schools',
}

const MotionSection = ({ children }: { children: React.ReactNode }) => (
  <motion.div
    variants={sectionAnimation}
    initial='hidden'
    whileInView='visible'
    viewport={{ once: true, amount: 0.2 }}
  >
    {children}
  </motion.div>
);

const Partnerships = () => {
  const searchParams = useSearchParams();


  const scrollToId = (id: string) => {
    const element = document.getElementById(id);

    if (!element) return;

    const offset = 160;

    const y = element.getBoundingClientRect().top + window.pageYOffset - offset;

    programmaticScrollTo({
      top: y,
      behavior: 'smooth',
    });
  };





  useEffect(() => {
    const id = searchParams.get('id');

    if (!id) return;

    setTimeout(() => {
      scrollToId(id);
    }, 200);
  }, [searchParams]);

  return (
    <AnimatePresence>
      <div className='xl:mt-[calc(44px)] lg:mt-[calc(32px)] md:mt-[calc(24px)] mt-[calc(32px)] mx-auto max-w-screen-2xl md:px-5 px-4'>
        <MotionSection>
          <TargetedForSection
            onSelect={(id) => {
              scrollToId(id);
            }}
            className='mb-8'
          />
        </MotionSection>

        <MotionSection>
          <HeroSection className='lg:px-0 xl:px-0' />
        </MotionSection>

        <div id={PartnershipIdEnum.AUTHORS}>
          <MotionSection>
            <ForAuthorsSection className='md:pt-5 pt-8 mb-[56px]' />
          </MotionSection>
        </div>

        <div id={PartnershipIdEnum.BRANDS}>
          <MotionSection>
            <ForBrandsSection className='mb-[56px]' />
          </MotionSection>
        </div>

        <div id={PartnershipIdEnum.PUBLISHERS}>
          <MotionSection>
            <ForPublishersSection className='mb-[56px]' />
          </MotionSection>
        </div>

        <div id={PartnershipIdEnum.CORPORATES}>
          <MotionSection>
            <ForCorporatesSection className='mb-[56px]' />
          </MotionSection>
        </div>

        <div id={PartnershipIdEnum.EDUCATORS}>
          <MotionSection>
            <ForEducatorsSection className='mb-[56px]' />
          </MotionSection>
        </div>

        <div id={PartnershipIdEnum.COLLEGES}>
          <MotionSection>
            <ForCollegesSection className='mb-[56px]' />
          </MotionSection>
        </div>

        <div id={PartnershipIdEnum.SCHOOLS}>
          <MotionSection>
            <ForSchoolsSection className='mb-[56px]' />
          </MotionSection>
        </div>
      </div>
    </AnimatePresence>
  );
};

export default Partnerships;
